// UserProfileContext.jsx
// Single source of truth for all tenant identity, persona names, and company config.
// Nothing in the UI hard-codes names like "Gary", "Exavalu", "Jarvis", or "Selma".
// Every component reads from this context instead.
//
// Multi-tenant design: each user has their own profile stored in localStorage.
// When this moves to a backend, replace localStorage with an API call in loadProfile().

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

// -- Default profile ---------------------------------------------------------
// Personal identity is deliberately EMPTY. It used to default to firstName 'Gary',
// which meant every signed-in user was greeted as Gary — reported 21 Aug 2026 by a
// user seeing "Good Morning, Gary" on their own account. The comment at the top of
// this file claimed nothing hard-codes names; this was the counter-example.
//
// It is now seeded from the authenticated session (see UserProfileProvider), so a
// new user gets their own name and an empty profile rather than someone else's.
export const DEFAULT_PROFILE = {
  // -- Personal identity ------------------------------------------------------
  firstName: '',
  lastName: '',
  email: '',
  title: 'Principal',
  avatarInitial: '',

  // -- Company identity -- TENANT DATA ----------------------------------------
  // `companyName` mirrors tenants.company_name and `companyDescriptor` mirrors
  // tenants.settings.company_descriptor; both are loaded from and written to the
  // tenant, never persisted locally (see TENANT_OWNED_FIELDS).
  //
  // Removed 5 Sep 2026 (P0.12 task 12.1), all three write-only and duplicating
  // data P0.10/P0.11 already owns:
  //   companyTagline — hint claimed "shown in the sidebar"; nothing rendered it.
  //                    The sidebar slot (Dashboard.jsx:293) shows companyName.
  //   industry       — replaced BY companyDescriptor, which is the same fact and
  //                    actually reaches the prompts. P0.10 created
  //                    company_descriptor with no UI at all, so this closes that
  //                    gap rather than deleting a field.
  //   targetMarket   — duplicate of commercial_profile.target_profile, which the
  //                    "Ideal Account Profile" field already edits.
  companyName: '',
  companyDescriptor: '',
  // P0.20 task 20.2 — the market sold INTO, as opposed to what the company IS.
  // Empty on purpose: unset means the personas drop industry-specific language
  // rather than asserting a market the tenant may not sell to.
  companyVertical: '',
  // B77 — what the company SELLS, the third distinct fact. Descriptor is what
  // you ARE, vertical is who you sell TO, this is what you sell THEM. Empty for
  // the same reason as the other two, with one extra consequence: the Brave
  // platform search is skipped entirely when it is blank rather than run broadly.
  companyServices: '',

  // -- Company intelligence -- TENANT DATA, loaded from /api/tenant/profile ---
  // Empty on purpose. These four fields shipped with one company's real profile
  // as their default ("Specializing in Guidewire implementations", "$500M-$5B in
  // written premium"), which meant a brand-new tenant opened Settings to a
  // filled-in form describing somebody else's business — and saving it would
  // have written that text to their tenant as if they had authored it.
  //
  // They are the only fields in this file that are NOT per-browser preferences:
  // ProfileSettingsPage loads them from the tenant and POSTs them back, so a
  // default here is a default for every tenant. See P0.11 in TODO.md.
  companyOfferings: '',
  approachToMarket: '',
  competitiveDifferentiators: '',
  targetAccountProfile: '',

  // -- Deal parameters -- TENANT DATA -----------------------------------------
  // B81. These shipped as ONE COMPANY'S numbers and became everyone's: the form
  // loaded them and POSTed them back on the first save, so three tenants ended
  // up holding byte-identical deal bands none of them chose. Gary — the tenant
  // most likely to have real bands — had null, because never opening the section
  // was the only way to avoid inheriting somebody else's pricing.
  //
  // This is exactly the defect P0.11 fixed for the company prose fields, forty
  // lines above, and it sat here unfixed because nothing read these values so
  // nothing looked wrong. They reach the persona prompts now, which is what
  // made it worth finding.
  //
  // Empty, not absent: the keys must exist or the selects go uncontrolled and
  // React warns on the first keystroke. Same reasoning as the prose fields.
  dealSizeRanges: [],
  defaultDealSize: '',
  tierDefinitions: { tier1: '', tier2: '', tier3: '' },

  // -- AI Persona names -------------------------------------------------------
  // These are the "stock" names. Each tenant can rename to anything they want.
  personas: {
    command: {
      name: 'Jarvis',
      description: 'Your executive AI -- orchestrates everything, always present',
      role: 'command',
      color: 'var(--dm-indigo-soft)',         // indigo
    },
    research: {
      name: 'Selma',
      description: 'Intelligence analyst -- research, signals, lead scoring',
      role: 'research',
      color: 'var(--dm-cyan)',         // cyan
    },
    strategy: {
      name: 'Victor',
      description: 'Strategist -- deal coaching, targeting, competitive framing',
      role: 'strategy',
      color: 'var(--dm-blue-deep)',         // blue
    },
    communications: {
      name: 'Cyrano',
      description: 'Communications -- emails, articles, proposals',
      role: 'communications',
      color: 'var(--dm-purple-vivid)',         // purple
    },
    // P0.20 task 20.1: Quinn was the only persona a tenant could not rename,
    // despite owning her own registry prompt (quinn.floor.system).
    workflow: {
      name: 'Quinn',
      description: 'Executive assistant -- calendar, tasks, the daily brief',
      role: 'workflow',
      color: 'var(--dm-green)',                // green
    },
  },

  // -- Platform preferences ---------------------------------------------------
  timezone: 'America/New_York',
  dateFormat: 'MM/DD/YYYY',
  weekStartsOn: 'Monday',

  // -- Metadata --------------------------------------------------------------
  profileVersion: 1,
  onboardingComplete: true,
};

// -- Context ------------------------------------------------------------------
const UserProfileContext = createContext(null);

const STORAGE_KEY = 'digitalme_user_profile';

// Fields that belong to the TENANT, not to this browser.
//
// They live in `tenants.settings.commercial_profile` and drive what every AI
// persona claims the company sells. localStorage must not hold them at all:
// a stored copy is authoritative on load (the merge below puts stored values
// OVER the defaults), so one browser's stale copy outlives any code change.
//
// That is not hypothetical. B52 emptied these four in DEFAULT_PROFILE, which
// fixed a fresh browser and did nothing for a browser that had already saved
// the old values — the form went on showing another company's profile under a
// banner correctly saying the tenant had none. Stripping them on read AND write
// is what actually removes the stale copy.
/**
 * Which identity fields a signed-in session imposes on a stored profile.
 *
 * Exported so tests can call the REAL rule. It used to live as a closure inside
 * the provider's useEffect, and src/context/profileIdentity.test.js mirrored it
 * by hand — so when the rule was wrong, the copy was wrong in the same way and
 * the tests passed. That is how a typed surname could be silently blanked
 * (5 Sep 2026).
 *
 * The rule: a KNOWN, DIFFERENT previous email means somebody else's data, so
 * personal identity is replaced. A blank previous email does not — it is a
 * profile predating stored email, or a form typed into before this async seed
 * resolved. Company and tenant settings are never touched here.
 */
export function seedIdentityFromSession(prev, sessionEmail, sessionFullName) {
  if (!prev || !sessionEmail) return prev;

  const sameUser = (prev.email || '').toLowerCase() === sessionEmail.toLowerCase();
  if (sameUser && prev.firstName) return prev;

  // P0.13 task 13.7 — the provider's name, when we have one.
  //
  // The derivation below is the whole reason the 5 Sep surname bug existed: an email
  // local-part CANNOT produce a surname. 'sunil.chitale@outlook.com' yields 'Sunil' and
  // nothing else, so lastName had no source and blanked on every switch and every race.
  //
  // Both OAuth providers hand over a real full name at sign-up, and migration 0020 now
  // keeps it — so on an account switch there is a correct surname available instead of
  // an empty string. `sessionFullName` is OPTIONAL and last: the two existing suites
  // call this with two arguments and assert the derived-from-address behaviour, which
  // stays exactly as it was whenever no name is supplied.
  const fromProvider = String(sessionFullName || '').trim();
  const parts = fromProvider ? fromProvider.split(/\s+/) : [];
  const providerFirst = parts[0] || '';
  // Everything after the first token. "Jim van der Berg" keeps "van der Berg" whole
  // rather than discarding the particles.
  const providerLast = parts.length > 1 ? parts.slice(1).join(' ') : '';

  const derived = sessionEmail.split('@')[0].split(/[._-]/)[0];
  const fallback = derived.charAt(0).toUpperCase() + derived.slice(1);
  const display = providerFirst || fallback;
  const switched = Boolean(prev.email) && !sameUser;

  return {
    ...prev,
    email: sessionEmail,
    firstName:     switched ? display           : (prev.firstName     || display),
    // On a switch the stored surname belongs to the PREVIOUS person, so it is replaced
    // rather than kept — with the new user's real surname if the provider gave us one,
    // and only otherwise with the empty string that used to be the only option.
    lastName:      switched ? providerLast      : (prev.lastName      || providerLast),
    avatarInitial: switched ? display.charAt(0) : (prev.avatarInitial || display.charAt(0)),
  };
}

export const TENANT_OWNED_FIELDS = [
  'companyName',
  'companyDescriptor',
  'companyVertical',
  'companyServices',   // B77
  'companyOfferings',
  'approachToMarket',
  'competitiveDifferentiators',
  'targetAccountProfile',
  // Deal parameters: commercial facts, not preferences. The bands shipped as one
  // company's pricing and applied to every tenant.
  'dealSizeRanges',
  'defaultDealSize',
  'tierDefinitions',
];

// Persona NAMES are tenant-owned; their descriptions, roles and colours are
// product copy that stays in code. So `personas` cannot simply be deleted —
// only the `name` inside each is stripped, or the persona list itself would
// vanish from localStorage and the UI would lose its labels entirely.
// Must stay in step with PERSONA_KEYS in api/tenant/profile.js and DEFAULTS in
// api/shared/personas.js. A key missing here is a name that never gets stripped
// on load, which is how pre-migration defaults reached a tenant once before.
const PERSONA_KEYS = ['command', 'research', 'strategy', 'communications', 'workflow'];

/** A copy with the tenant-owned fields removed, for persisting locally. */
export function withoutTenantFields(profile) {
  const out = { ...profile };
  for (const f of TENANT_OWNED_FIELDS) delete out[f];

  if (out.personas) {
    out.personas = { ...out.personas };
    for (const k of PERSONA_KEYS) {
      if (!out.personas[k]) continue;
      const { name, ...rest } = out.personas[k];   // eslint-disable-line no-unused-vars
      out.personas[k] = rest;
    }
  }
  return out;
}

// -- Provider -----------------------------------------------------------------
export function UserProfileProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load profile from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        // Discard any tenant-owned fields a previous version persisted here.
        // ProfileSettingsPage loads the real values from /api/tenant/profile.
        const parsed = withoutTenantFields(JSON.parse(stored));
        // Deep merge with defaults to handle new fields added in updates
        const merged = deepMerge(DEFAULT_PROFILE, parsed);
        // Auto-update timezone from browser if not explicitly set by user
        if (!parsed.timezoneSetByUser) {
          const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (browserTz) merged.timezone = browserTz;
        }
        setProfile(merged);
      } else {
        // First run -- detect timezone from browser
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const initial = { ...DEFAULT_PROFILE, timezone: browserTz || DEFAULT_PROFILE.timezone };
        setProfile(initial);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(withoutTenantFields(initial)));
      }

      // Seed identity from the signed-in session.
      //
      // The store is a single localStorage key shared by whoever uses this
      // browser, so the SESSION — not whatever happens to be in storage — is the
      // authority on who the user is.
      //
      // This used to seed only when `firstName` was empty, which covers a
      // first-ever sign-in but not an account SWITCH. Signing in as
      // chitale.sunil@gmail.com on a browser still holding the profile for
      // Sunil.Chitale@outlook.com showed the previous account's name and email
      // on the dashboard: the guard saw a firstName and returned early.
      // Reported 1 Sep 2026, the day Google sign-in started working. Same class
      // as the `firstName: 'Gary'` default fixed 21 Aug 2026.
      //
      // The test is now whether the session's email matches the profile's. Only
      // PERSONAL IDENTITY is reset — company name, offerings, persona names and
      // every other tenant setting are deliberately left alone, because this key
      // is their only home (nothing persists them server-side) and clearing them
      // on an account switch would destroy real work.
      supabase.auth.getSession().then(({ data }) => {
        const email = data?.session?.user?.email;
        if (!email) return;
        // The provider's own name, straight off the session — the same value migration
        // 0020's trigger writes to public.users.full_name, so no API call is needed to
        // read it here. Undefined for a hand-created account, which is exactly when the
        // email-derived fallback below should still apply.
        const fullName = data?.session?.user?.user_metadata?.full_name;
        setProfile((prev) => {
          // Seed from STORAGE, not from `prev`.
          //
          // This resolves asynchronously and can win the race against the
          // setProfile(merged) above — the provider remounts whenever AuthGate
          // briefly returns null, which a token refresh causes. When it does,
          // `prev` is the un-loaded initial state, so the seeder saw
          // firstName:'' and email:'', treated it as an unseeded profile, and
          // rewrote identity from the email address: firstName 'Sunil' (from
          // sunil.chitale@…) and lastName '' — then PERSISTED that over the
          // stored "Chitale".
          //
          // title/timezone survived because the seeder never touches them, and
          // avatarInitial survived because the derivation reproduces it. Only
          // lastName has no fallback, which is why it alone kept vanishing.
          //
          // Storage is the authority here: it is what the previous session
          // committed, and it cannot be mid-flight.
          let base = prev;
          try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) base = deepMerge(DEFAULT_PROFILE, withoutTenantFields(JSON.parse(raw)));
          } catch { /* fall back to prev */ }

          const next = seedIdentityFromSession(base, email, fullName);
          if (next === base && base === prev) return prev;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(withoutTenantFields(next)));
          return next;
        });
      }).catch(() => {});
    } catch {
      setProfile(DEFAULT_PROFILE);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save any profile changes to localStorage
  const updateProfile = useCallback((updates) => {
    setProfile(prev => {
      const next = deepMerge(prev, updates);
      // In-memory state keeps the tenant fields so the form stays controlled;
      // only the persisted copy drops them.
      localStorage.setItem(STORAGE_KEY, JSON.stringify(withoutTenantFields(next)));
      return next;
    });
  }, []);

  // Update a single persona name
  const updatePersonaName = useCallback((role, name) => {
    setProfile(prev => {
      const next = {
        ...prev,
        personas: {
          ...prev.personas,
          [role]: { ...prev.personas[role], name },
        },
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(withoutTenantFields(next)));
      return next;
    });
  }, []);

  // Reset to defaults (useful for demo/testing)
  const resetProfile = useCallback(() => {
    setProfile(DEFAULT_PROFILE);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(withoutTenantFields(DEFAULT_PROFILE)));
  }, []);

  // Convenience getters -- components can use these directly
  const p = profile || DEFAULT_PROFILE;
  const helpers = {
    // Persona names
    commandName:       p.personas?.command?.name       || 'Jarvis',
    researchName:      p.personas?.research?.name      || 'Selma',
    strategyName:      p.personas?.strategy?.name      || 'Victor',
    commsName:         p.personas?.communications?.name || 'Cyrano',
    // P0.30 30.2 — `workflow` HAS BEEN RENAMEABLE AND UNRESOLVABLE.
    // ProfileSettingsPage.jsx:537 sends personas.workflow.name and tenant
    // settings store it ("workflow": "Quinn" is in persona_names today), but no
    // helper ever exposed it — so a tenant could rename this persona and every
    // screen kept saying Quinn. The other four have had a resolver all along.
    workflowName:      p.personas?.workflow?.name       || 'Quinn',

    // Identity
    firstName:         p.firstName || 'there',
    fullName:          [p.firstName, p.lastName].filter(Boolean).join(' '),
    companyName:       p.companyName || 'Your Company',
    email:             p.email || '',
    avatarInitial:     p.avatarInitial || (p.firstName?.[0] || '?'),

    // Timezone
    timezone: p.timezone || DEFAULT_PROFILE.timezone,

    // Greeting
    greeting: () => {
      const h = new Date().getHours();
      const name = p.firstName || 'there';
      if (h < 12) return `Good morning, ${name}.`;
      if (h < 17) return `Good afternoon, ${name}.`;
      return `Good evening, ${name}.`;
    },
  };

  return (
    <UserProfileContext.Provider value={{
      profile: p,
      isLoading,
      updateProfile,
      updatePersonaName,
      resetProfile,
      ...helpers,
    }}>
      {!isLoading && children}
    </UserProfileContext.Provider>
  );
}

// -- Hook ---------------------------------------------------------------------
export function useProfile() {
  const ctx = useContext(UserProfileContext);
  if (!ctx) throw new Error('useProfile must be used inside UserProfileProvider');
  return ctx;
}

// -- Utility: deep merge objects -----------------------------------------------
function deepMerge(base, override) {
  const result = { ...base };
  for (const key of Object.keys(override)) {
    if (
      override[key] !== null &&
      typeof override[key] === 'object' &&
      !Array.isArray(override[key]) &&
      typeof base[key] === 'object' &&
      base[key] !== null &&
      !Array.isArray(base[key])
    ) {
      result[key] = deepMerge(base[key], override[key]);
    } else {
      result[key] = override[key];
    }
  }
  return result;
}
